/**
 * Browser-persisted visitor identity and message allowance (docs/14-ABUSE-PROTECTION.md, layer 1).
 *
 * - Identity: 32 bytes from the browser's CSPRNG, hex-encoded, kept in localStorage. No personal
 *   data, no fingerprinting; the server validates the format only.
 * - Allowance: the number of AI messages this browser has sent, also in localStorage. Reloads,
 *   "New search", conversation resets, closing/reopening the widget, navigating within the site
 *   and later visits all keep it. The widget refuses to send a request once it is used up.
 *
 * Honest limitation: this is a product limit for normal visitors, not a security boundary. A
 * user who clears or edits browser storage (or uses another browser/profile) starts again.
 * Automated abuse of the API is the job of the hosting-side IP protection, not of this counter.
 */
const ID_KEY = "villamine-ai:visitor";
const USED_KEY = "villamine-ai:visitor-used";
const HEX = /^[a-f0-9]{64}$/;
let memoryId: string | null = null;
let memoryUsed = 0;

function storage(win: Window | null | undefined): Storage | null {
  try {
    return win?.localStorage ?? null;
  } catch {
    return null;
  }
}

function generate(win: Window | null | undefined): string {
  const bytes = new Uint8Array(32);
  const c = (win as (Window & { crypto?: Crypto }) | null | undefined)?.crypto ?? (globalThis as { crypto?: Crypto }).crypto;
  if (!c?.getRandomValues) throw new Error("no secure random source");
  c.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

export function getVisitorId(win: Window | null | undefined): string | null {
  const ls = storage(win);
  try {
    const existing = ls?.getItem(ID_KEY);
    if (existing && HEX.test(existing)) return existing;
  } catch {
    /* blocked storage */
  }
  if (memoryId) return memoryId;
  let id: string;
  try {
    id = generate(win);
  } catch {
    return null;
  }
  try {
    ls?.setItem(ID_KEY, id);
  } catch {
    /* quota or blocked: keep it for this page only */
  }
  memoryId = id;
  return id;
}

/** Messages this browser has already used (never negative, never above what was stored). */
export function getUsedMessages(win: Window | null | undefined): number {
  try {
    const raw = storage(win)?.getItem(USED_KEY);
    if (raw !== null && raw !== undefined) {
      const n = Number(raw);
      if (Number.isFinite(n) && n >= 0) return Math.floor(n);
    }
  } catch {
    /* blocked storage */
  }
  return memoryUsed;
}

function setUsed(win: Window | null | undefined, n: number): void {
  memoryUsed = Math.max(0, n);
  try {
    storage(win)?.setItem(USED_KEY, String(memoryUsed));
  } catch {
    /* blocked storage: in-memory only for this page */
  }
}

export function remainingMessages(win: Window | null | undefined, limit: number): number {
  return Math.max(0, limit - getUsedMessages(win));
}

/** Counts one message before it is sent; returns the new remaining number. */
export function consumeMessage(win: Window | null | undefined, limit: number): number {
  setUsed(win, getUsedMessages(win) + 1);
  return remainingMessages(win, limit);
}

/** Gives a message back when the request never entered the AI pipeline (rejected, rate-limited, network error). */
export function refundMessage(win: Window | null | undefined, limit: number): number {
  setUsed(win, getUsedMessages(win) - 1);
  return remainingMessages(win, limit);
}

/** Test hook. */
export function resetVisitorForTests(): void {
  memoryId = null;
  memoryUsed = 0;
}
